import React, { useState } from 'react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Copy, QrCode, Link2, Mail, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const FRONTEND_URL = window.location.origin;

export default function RecibiLinkGenerator({ isOpen, onClose, employeeData }) {
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  const generateToken = async () => {
    if (!employeeData) {
      toast.error('Datos de empleado no disponibles');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipts/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeData.id,
          nombre_completo: employeeData.nombre_completo,
          email: employeeData.email,
          lugar_trabajo: employeeData.lugar_trabajo,
          expires_in_hours: 72
        })
      });

      const data = await res.json();

      if (data.success) {
        setTokenData(data);
        toast.success('Link generado correctamente', {
          description: 'Válido por 72 horas'
        });
      } else {
        toast.error('Error al generar el link');
      }
    } catch (error) {
      toast.error('Error al generar el link', { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `QR-Recibi-${employeeData?.nombre_completo || 'empleado'}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
      
      toast.success('QR descargado correctamente');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const sendByEmail = async () => {
    if (!tokenData || !employeeData?.email) {
      toast.error('Email no disponible');
      return;
    }

    toast.info('Funcionalidad de envío por email próximamente', {
      description: 'Por ahora, copia el link y envíalo manualmente'
    });
  };

  const fullLink = tokenData ? `${FRONTEND_URL}/recibi/${tokenData.token}` : '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: '600px' }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: '22px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <QrCode style={{ width: '24px', height: '24px' }} />
            Generar Link de Recibí
          </DialogTitle>
          <DialogDescription>
            {employeeData ? (
              <>Crea un link único para que <strong>{employeeData.nombre_completo}</strong> rellene el formulario de recibí</>
            ) : (
              'Selecciona un empleado primero'
            )}
          </DialogDescription>
        </DialogHeader>

        <div style={{ padding: '20px 0' }}>
          {!tokenData ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  margin: '0 auto 16px', 
                  borderRadius: '50%', 
                  background: '#f0f8f2', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Link2 style={{ width: '36px', height: '36px', color: '#2d8a4e' }} />
                </div>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                  Genera un link seguro válido por <strong>72 horas</strong>
                </p>
                <p style={{ fontSize: '13px', color: '#999' }}>
                  El empleado podrá acceder al formulario sin login
                </p>
              </div>

              {employeeData && (
                <Card style={{ marginBottom: '20px', background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <CardContent style={{ padding: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#666', textAlign: 'left' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Empleado:</strong> {employeeData.nombre_completo}
                      </div>
                      {employeeData.email && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong>Email:</strong> {employeeData.email}
                        </div>
                      )}
                      <div>
                        <strong>Lugar:</strong> {employeeData.lugar_trabajo}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button
                onClick={generateToken}
                disabled={loading || !employeeData}
                style={{ 
                  background: loading ? '#ccc' : '#2d8a4e', 
                  color: '#fff', 
                  minWidth: '200px' 
                }}
              >
                {loading ? 'Generando...' : 'Generar Link y QR'}
              </Button>
            </div>
          ) : (
            <div>
              {/* QR Code */}
              <div style={{ textAlign: 'center', marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
                <QRCodeSVG 
                  id="qr-code-svg"
                  value={fullLink}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
                  Escanea con el móvil para acceder al formulario
                </p>
              </div>

              {/* Link */}
              <div style={{ marginBottom: '20px' }}>
                <Label style={{ marginBottom: '8px', display: 'block', fontSize: '13px', fontWeight: 600 }}>
                  Link Directo
                </Label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    value={fullLink}
                    readOnly
                    style={{ fontSize: '13px', fontFamily: 'monospace' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copyToClipboard(fullLink)}
                    style={{ minWidth: '100px' }}
                  >
                    <Copy style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                    Copiar
                  </Button>
                </div>
              </div>

              {/* Expiration Info */}
              <Card style={{ marginBottom: '20px', background: '#fff3cd', border: '1px solid #ffc107' }}>
                <CardContent style={{ padding: '12px' }}>
                  <p style={{ fontSize: '12px', color: '#856404', margin: 0 }}>
                    ⏱️ Este link expira el <strong>{new Date(tokenData.expires_at).toLocaleString('es-ES')}</strong>
                  </p>
                </CardContent>
              </Card>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadQR}
                  style={{ flex: 1 }}
                >
                  <Download style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                  Descargar QR
                </Button>
                {employeeData?.email && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={sendByEmail}
                    style={{ flex: 1 }}
                  >
                    <Mail style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                    Enviar por Email
                  </Button>
                )}
              </div>

              {/* Generate New */}
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setTokenData(null)}
                  style={{ fontSize: '13px' }}
                >
                  Generar Nuevo Link
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
